import classNames from 'classnames';
import React, { useMemo } from 'react';

import { TheaterReservationTracking } from 'common/constants/trackingEventsNames';
import getVersionLabel, {
  ShowtimeGroupKey
} from 'common/tools/showtime/getVersionLabel';
import {
  DayBlockPosition,
  groupShowtimesByDayBlocks
} from 'common/tools/showtime/groupShowtimesByDayBlocks';
import trans from 'common/tools/translations/trans';

import { PartialShowtime } from 'website/containers/showtimes/utils/types';

import ShowtimesVersion, { OpenModalShowtimes } from './ShowtimesVersion';

type ShowtimesVersionHolderProps = {
  additionalClassName?: string | null;
  currentDay?: string;
  hideDate?: boolean;
  openModal?: OpenModalShowtimes;
  showtimes: Partial<Record<ShowtimeGroupKey, PartialShowtime[]>>;
  theaterHasBooking?: boolean | null;
  tracking: Omit<TheaterReservationTracking, 'showtime'>;
  versionHint?: React.ReactNode;
};

const DAY_BLOCK_POSITIONS: DayBlockPosition[] = ['before', 'standard', 'after'];

const VERSION_ORDER: ShowtimeGroupKey[] = [
  'dubbed',
  'local',
  'multiple',
  'original',
  'dubbed_st',
  'local_st',
  'multiple_st',
  'original_st',
  'dubbed_sme',
  'local_sme',
  'multiple_sme',
  'original_sme'
];

const ShowtimesVersionHolder = ({
  additionalClassName,
  currentDay,
  hideDate,
  openModal,
  showtimes,
  theaterHasBooking,
  tracking,
  versionHint
}: ShowtimesVersionHolderProps) => {
  const sortedShowtimes = useMemo(() => {
    return Object.entries(showtimes).sort(([keyA], [keyB]) => {
      const indexA = VERSION_ORDER.indexOf(keyA as ShowtimeGroupKey);
      const indexB = VERSION_ORDER.indexOf(keyB as ShowtimeGroupKey);

      if (indexA !== -1 && indexB === -1) return -1;
      if (indexA === -1 && indexB !== -1) return 1;
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;

      return keyA.localeCompare(keyB);
    });
  }, [showtimes]);

  // the positions each version has blocks at, so only those render a ShowtimesVersion
  const positionsByVersion = useMemo(
    () =>
      new Map(
        sortedShowtimes.map(([versionKey, hours]) => [
          versionKey,
          new Set(
            hours && hours.length > 0
              ? groupShowtimesByDayBlocks(hours, currentDay).map(
                  block => block.position
                )
              : []
          )
        ])
      ),
    [sortedShowtimes, currentDay]
  );

  const showtimesVersions = (
    <>
      {/* the blocks of every version are listed position by position (nights that ended on the
          viewed day, standard showtimes, nights that start on it) to keep the list chronological:
          see sortBlocksByPosition, ALLOCINE-15181 */}
      {DAY_BLOCK_POSITIONS.map(position =>
        sortedShowtimes.map(([versionKey, hours]) => {
          if (
            !hours ||
            hours.length === 0 ||
            !positionsByVersion.get(versionKey)?.has(position)
          ) {
            return null;
          }

          const label = getVersionLabel(versionKey as ShowtimeGroupKey);

          return (
            <ShowtimesVersion
              currentDay={currentDay}
              hideDate={hideDate}
              key={`${position}-${versionKey}`}
              openModal={openModal}
              position={position}
              showtimesHours={hours}
              showtimesLabel={label}
              tracking={tracking}
              versionHint={versionHint}
            />
          );
        })
      )}

      {theaterHasBooking && !versionHint && (
        <p className="e-ticket icon icon-ticket">
          <span className="e-ticket-text">
            {trans('moviepage_showtimes.e-ticket.text')}
          </span>
        </p>
      )}
    </>
  );

  const finalClassName = classNames(
    'showtimes-versions-holder',
    additionalClassName
  );

  return <div className={finalClassName}>{showtimesVersions}</div>;
};

export default ShowtimesVersionHolder;
